/* screens/academy/CoursePlayer.jsx — плеер курса (/learning/c/:id).
   Sticky прогресс-шапка + рельс программы (инлайн на десктопе, Drawer на мобиле) +
   читаемый урок (видео/проза/материалы) + тест/ДЗ + sticky-футер навигации.
   Вёрстка на классах cp- / quiz- / opt- / hw- (styles/academy.css). */
import React from 'react';
import { PageHeader } from '../../components/patterns/PageHeader.jsx';
import { QueryBoundary, EmptyState } from '../../components/patterns/States.jsx';
import { Drawer } from '../../components/ui/Drawer.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Field, Textarea } from '../../components/ui/Field.jsx';
import { ChatMarkdown } from '../../components/patterns/chat/Markdown.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { useBp } from '../../app/providers/BreakpointProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import { RR } from '../../lib/router.jsx';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { tariffTarget } from '../../lib/aiAccess.js';
import * as api from '../../lib/api/academy.js';
import { academyErrorText, lessonAccessNotice } from '../../lib/academy/errorCodes.js';
import { fmtAccessDate, findMyEnrollment, playerAccessView } from '../../lib/academy/access.js';
import { isFeedbackClosed, readQuizScore } from '../../lib/academy/lessonState.js';
import { nextLessonIdFromTree } from '../../lib/academy/resume.js';
import { liveSearch, withLesson, withoutPurchase } from '../../lib/academy/playerParams.js';
import { BuyDialog } from './BuyDialog.jsx';

const { useState, useEffect, useCallback, useRef } = React;
const { useFakeQuery } = hooks;
const { useParams, useSearchParams, useNavigate } = RR;

// тип урока → иконка/метка (показывается, когда урок не пройден и не закрыт)
const TYPE_ICON = { video: 'camera', quiz: 'check', homework: 'edit', text: 'book' };
const LOCK_HINT = { drip: 'Откроется позже', tariff: 'Не входит в ваш тариф',
  sequential: 'Сначала пройдите предыдущие уроки',
  subscription: 'Подписка Restorium не активна — продлите доступ',
  frozen: 'Доступ к курсу временно заморожен' };
const TYPE_LABEL = { video: 'Видео', quiz: 'Тест', homework: 'Задание', text: 'Урок' };
// статус ДЗ (HomeworkSubmission.status) → тон/метка Pill. Используется и в рельсе
// (бейдж урока, B1fe), и в шапке HomeworkPane — один источник правды (без новых цветов).
const SUB_TONE = { submitted: 'accent', accepted: 'ok', revision: 'warn', rejected: 'bad' };
const SUB_LABEL = { submitted: 'На проверке', accepted: 'Принято', revision: 'На доработку', rejected: 'Отклонено' };
// Интервал хартбита просмотра урока (B6) — фронт шлёт приращение секунд раз в ~30с;
// клиентский кап (бэк всё равно клампит до своего HEARTBEAT_MAX_DELTA ≈120с).
const HEARTBEAT_INTERVAL_MS = 30000;
const HEARTBEAT_MAX_DELTA = 120;

function money(v) {
  return new Intl.NumberFormat('ru-RU').format(Math.round(Number(v || 0))) + ' ₽';
}

// ── ST-15: что считается «незаконченным вводом ученика» ─────────────────────
// Урок перечитывается по возврату во вкладку (вердикт куратора обязан доехать без
// ручной перезагрузки). Но перечитка меняет проп `quiz`/`hw` под открытой формой:
// при shuffle_questions сервер отдаёт вопросы в ДРУГОМ порядке, и они переставятся
// у человека под руками, а перечитка во время отправки конкурирует с ней. Поэтому
// зоны с живым вводом сообщают о себе, и пока хоть одна «грязная» — не перечитываем.
// Чистые функции: проверяются юнитом, без браузера.
function quizIsDirty({ busy, answers, result } = {}) {
  // busy — летит проверка; result — открыт разбор попытки; answers — отмеченные
  // варианты, которых ещё нет на сервере.
  return !!busy || !!result || Object.keys(answers || {}).length > 0;
}
function homeworkIsDirty({ busy, body, files } = {}) {
  // busy — летит сдача; текст ответа и приложенные файлы живут только в браузере.
  return !!busy || !!String(body == null ? '' : body).trim() || (files || []).length > 0;
}
function lessonRefetchAllowed(dirty) {
  if (!dirty) return true;
  return !dirty.quiz && !dirty.homework;
}

function lessonType(l) {
  if (l.has_quiz) return 'quiz';
  if (l.has_homework) return 'homework';
  if (l.has_video) return 'video';
  return 'text';
}

// ── экран «Доступ заморожен» (#6) ────────────────────────────────────────────
// Показывается, когда запись ученика на паузе: уроки временно недоступны,
// таймер доступа стоит, после разморозки срок сдвигается на длительность паузы.
// Тела уроков НЕ рендерим. Используем общий шаблон состояния (EmptyState) —
// без новых CSS-классов, чтобы оставаться в рамках scope этого экрана.
function FrozenNotice() {
  return (
    <EmptyState icon="pause" serif={false} title="Доступ заморожен"
      body="Доступ к курсу временно приостановлен — уроки недоступны, пока действует пауза. Таймер доступа остановлен: после разморозки срок продлится на время паузы. По вопросам обратитесь к организатору обучения." />
  );
}

// Подписка кончилась. Отдельный экран, а не FrozenNotice: там обещают продление
// срока после разморозки, которого здесь не будет, и не называют настоящую
// причину — человек пошёл бы к организатору обучения вместо продления подписки.
function SubscriptionHoldNotice({ onRenew }) {
  return (
    <EmptyState icon="credit-card" serif={false} title="Подписка Restorium не активна"
      body="Этот курс открыт по подписке. Пока она не продлена, уроки недоступны — но ничего не потеряно: прогресс, сданные работы и баллы за тесты сохранены. После продления обучение продолжится с того же места."
      /* Выход обязателен: соседний экран «Доступ истёк» кнопку даёт, и без неё
         ученик читает «продлите подписку», оставаясь в рельсе из мёртвых
         кнопок. Ведём в подписку пространства, а не в покупку курса: курс он не
         покупал, у него кончилась подписка.
         Кнопка появляется, только если есть куда вести: выдача по REACH_TEAM
         достаёт до сотрудников, а платит владелец пространства — сотруднику
         предлагать «продлить» значит посылать его в чужой кабинет. */
      action={onRenew
        ? <Button variant="primary" onClick={onRenew}>Продлить подписку</Button>
        : null} />
  );
}

// ── экран «урок закрыт» с ПРАВИЛЬНОЙ причиной ────────────────────────────────
// Раньше любой 403 на уроке рисовал FrozenNotice — ученику с последовательным
// локом называли чужую причину (пауза) и обещали продление срока, которого не
// будет. Текст берём из реестра кодов (lib/academy/errorCodes.js), а не
// придумываем здесь: он же зеркалит коды бэкенда.
function LessonLockedNotice({ notice, onProgram, onUpgrade }) {
  // PL-12: у отказа `locked_tariff` подсказка обещает «перейдите на тариф с полной
  // программой» — и до этой правки обещала НЕОСУЩЕСТВИМОЕ: доплата в диалоге
  // покупки была реализована, но ни один вход её активному ученику не открывал
  // (каталог показывает «Записан», лендинг гейтит истёкшим доступом). Здесь —
  // единственное место, где ученик упирается в границу своего тарифа, значит и
  // выход обязан быть здесь.
  return (
    <EmptyState icon={notice.icon} serif={false} title={notice.title} body={notice.body}
      action={(onUpgrade || onProgram) && (
        <>
          {onUpgrade && (
            <Button variant="primary" iconLeft="sparkles" onClick={onUpgrade}>
              Перейти на старший тариф
            </Button>
          )}
          {onProgram && (
            <Button variant="secondary" iconLeft="menu" onClick={onProgram}>Программа курса</Button>
          )}
        </>
      )} />
  );
}

// ── экран «Доступ истёк» (ST-3) ──────────────────────────────────────────────
// Дерево при истёкшем доступе отдаёт enrollment:null, и плеер молча редиректил
// на лендинг, а лендинг звал «Продолжить» обратно в плеер — бесконечный
// пинг-понг без единого слова о причине. Теперь состояние названо своим именем,
// и продление начинается прямо отсюда (для платного курса — тот же BuyDialog).
function AccessExpiredNotice({ expiresAt, onRenew, busy }) {
  const d = fmtAccessDate(expiresAt);
  return (
    <EmptyState icon="clock" serif={false} title="Доступ истёк"
      body={(d ? `Доступ к курсу действовал до ${d}. ` : '')
        + 'Уроки закрыты, но пройденное сохранено — после продления вы продолжите с того же места.'}
      action={<Button variant="primary" loading={busy} onClick={onRenew}>Продлить доступ</Button>} />
  );
}

// ── рельс программы курса ───────────────────────────────────────────────────
function Rail({ tree, activeId, onPick, inDrawer }) {
  return (
    <nav className={'cp-rail' + (inDrawer ? ' cp-rail--in-drawer' : '')} aria-label="Программа курса">
      {(tree.modules || []).map((m) => {
        const total = m.lessons.length;
        const done = m.lessons.filter((l) => l.progress === 'completed').length;
        return (
          <div key={m.id} className="cp-rail__module">
            <div className="cp-rail__module-head">
              <span className="t-eyebrow">{m.title}</span>
              <span className="cp-rail__module-count">{done}/{total}</span>
            </div>
            {/* AD-12: методист пишет описание модуля с подсказкой «видит ученик
                в программе курса» — значит, оно обязано быть видно. Рельс рисовал
                только заголовок, и поле уходило в никуда. */}
            {m.summary && <p className="cp-rail__module-summary">{m.summary}</p>}
            <div className="cp-rail__lessons">
              {m.lessons.map((l) => {
                const locked = !!l.lock;
                const isDone = l.progress === 'completed';
                const active = l.id === activeId;
                // Статус ДЗ из дерева (B1fe): submitted|accepted|revision|rejected|null.
                // Бейдж/подсветка только когда статус есть; реестр тонов/меток — общий SUB_*.
                const hwStatus = SUB_LABEL[l.homework_status] ? l.homework_status : null;
                const icon = locked ? 'lock' : isDone ? 'check-circle' : TYPE_ICON[lessonType(l)];
                const cls = 'cp-lesson' + (active ? ' is-active' : '') + (isDone ? ' is-done' : '')
                  + (locked ? ' is-locked' : '') + (hwStatus ? ' has-hw is-hw-' + hwStatus : '');
                return (
                  <button key={l.id} type="button" className={cls} disabled={locked}
                    aria-current={active ? 'true' : undefined}
                    title={locked ? (LOCK_HINT[l.lock] || 'Сначала пройдите предыдущие уроки')
                      : (hwStatus ? 'ДЗ: ' + SUB_LABEL[hwStatus] : undefined)}
                    aria-label={l.title + (locked ? ' (закрыт)' : isDone ? ' (пройден)' : '')
                      + (hwStatus ? ' — ДЗ: ' + SUB_LABEL[hwStatus] : '')}
                    onClick={() => !locked && onPick(l.id)}>
                    <span className="cp-lesson__icon"><Icon name={icon} size={16} /></span>
                    <span className="cp-lesson__title">{l.title}</span>
                    {hwStatus && (
                      <span className="cp-lesson__tag">
                        <Pill tone={SUB_TONE[hwStatus]} size="sm" glyph={false}>{SUB_LABEL[hwStatus]}</Pill>
                      </span>
                    )}
                    {/* Плашка «бонус» — про СОДЕРЖАНИЕ урока (сырой флаг методиста),
                        поэтому строго по `is_bonus`. По `is_required` она врала:
                        то поле про зачёт ЭТОЙ записи и обнуляется тарифом, из-за
                        чего обязательные уроки модуля вне тарифа подписывались
                        «бонус». Причину блокировки несёт замок + title (LOCK_HINT). */}
                    {l.is_bonus && <span className="cp-lesson__tag"><Pill tone="neutral" size="sm" glyph={false}>бонус</Pill></span>}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </nav>
  );
}

// ── тест ────────────────────────────────────────────────────────────────────
// verdict: null (до проверки) | 'correct' (выбран и верен) | 'incorrect' (выбран,
// неверен) | 'missed' (правильный, но НЕ выбран). selected = что выбрал ученик.
function OptBtn({ multi, selected, verdict, disabled, onPick, children }) {
  const cls = 'opt-btn'
    + (selected && !verdict ? ' is-selected' : '')
    + (verdict ? ' is-' + verdict : '');
  const glyph = verdict === 'incorrect' ? 'x-circle' : (verdict ? 'check-circle' : null);
  return (
    <button type="button" className={cls} disabled={disabled}
      role={multi ? 'checkbox' : 'radio'} aria-checked={selected} onClick={onPick}>
      <span className={'opt-btn__mark' + (multi ? ' opt-btn__mark--multi' : '')} aria-hidden="true" />
      <span className="opt-btn__label">{children}</span>
      {verdict === 'missed' && <span className="opt-btn__tag opt-btn__tag--missed">Пропущено</span>}
      {glyph && <span className="opt-btn__glyph"><Icon name={glyph} size={16} /></span>}
    </button>
  );
}

function QuizRunner({ quiz, onPassed, onDirty }) {
  const toast = useToast();
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [attemptsUsed, setAttemptsUsed] = useState(quiz.attempts_used || 0);
  // ST-5: «Запросить пересдачу» — ученик не сбрасывает попытки сам (иначе
  // max_attempts теряет смысл), а ставит запрос в очередь куратора.
  // Начальное значение — из `retake_requested` дерева урока: после перезагрузки
  // страницы кнопка обязана оставаться в состоянии «Запрос отправлен», иначе
  // ученик шлёт заявку повторно и не понимает, дошла ли первая.
  const [retakeAsked, setRetakeAsked] = useState(!!quiz.retake_requested);
  const [retakeBusy, setRetakeBusy] = useState(false);
  const passed = quiz.passed || (result && result.passed);
  const noAttemptsLeft = quiz.max_attempts > 0 && attemptsUsed >= quiz.max_attempts && !passed;
  const resById = {};
  (result?.results || []).forEach((r) => { resById[r.question_id] = r; });

  // ST-15: пока ученик отвечает / летит проверка / открыт разбор — родитель не
  // перечитывает урок по возврату во вкладку. Наверх поднимаем ОДИН признак, а не
  // всё состояние теста: LessonPane решает только «можно ли сейчас перечитать».
  const dirty = quizIsDirty({ busy, answers, result });
  useEffect(() => {
    if (!onDirty) return undefined;
    onDirty(dirty);
    // Уход со шага (тест размонтирован) обязан снимать блокировку — иначе
    // залипший признак навсегда выключил бы refetch по фокусу.
    return () => onDirty(false);
  }, [onDirty, dirty]);

  const set = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));
  const toggleMulti = (qid, opt) => setAnswers((a) => {
    const cur = new Set(a[qid] || []);
    cur.has(opt) ? cur.delete(opt) : cur.add(opt);
    return { ...a, [qid]: [...cur] };
  });

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.submitQuiz(quiz.id, answers);
      setResult(r);
      if (r.attempt_no) setAttemptsUsed(r.attempt_no);
      if (r.passed) { toast.show(`Тест сдан — ${r.percent}%`, 'success'); onPassed && onPassed(); }
      else toast.show(`Не сдан — ${r.percent}% (порог ${quiz.pass_threshold}%)`, 'warning');
    } catch (e) { toast.show(academyErrorText(e, 'Не удалось отправить'), 'danger'); }
    finally { setBusy(false); }
  }, [quiz, answers, toast, onPassed]);

  const requestRetake = useCallback(async () => {
    setRetakeBusy(true);
    try {
      await api.requestQuizRetake(quiz.id);
      setRetakeAsked(true);
      toast.show('Запрос на пересдачу отправлен куратору', 'success');
    } catch (e) { toast.show(academyErrorText(e, 'Не удалось отправить запрос'), 'danger'); }
    finally { setRetakeBusy(false); }
  }, [quiz.id, toast]);

  // «Попробовать ещё раз» — чистая пересдача: убираем разбор И прошлые ответы
  // (снимаем все отметки), отвечаем заново с нуля.
  const retry = useCallback(() => { setResult(null); setAnswers({}); }, []);

  // вердикт варианта после проверки (show_correct): выбор × правильность
  const optVerdict = (q, optId) => {
    const r = resById[q.id];
    if (!r || !('correct_answer' in r)) return null;
    const ca = Array.isArray(r.correct_answer) ? r.correct_answer.map(String) : [];
    const isAns = ca.includes(String(optId));
    const picked = q.qtype === 'multi'
      ? (answers[q.id] || []).map(String).includes(String(optId))
      : String(answers[q.id]) === String(optId);
    if (isAns && picked) return 'correct';   // выбрал верный
    if (isAns && !picked) return 'missed';    // верный, но пропустил
    if (!isAns && picked) return 'incorrect'; // выбрал неверный
    return null;
  };
  // правильный ответ текст/число (когда ученик ответил неверно)
  const correctAnswerText = (q) => {
    const r = resById[q.id];
    const ca = r && r.correct_answer;
    if (q.qtype === 'number' && ca && typeof ca === 'object') {
      return ca.tolerance ? `${ca.value} (±${ca.tolerance})` : String(ca.value);
    }
    return Array.isArray(ca) ? ca.join(' / ') : '';
  };

  // ST-8: число верных берём из ОТВЕТА (correct_count/total), разбор — только
  // фолбэк. При show_correct=false results:null, и подсчёт по массиву печатал
  // «Верно 0 из N — порог пройден» даже сдавшему. correctCount === null =
  // «неизвестно», такую строку не выводим совсем, чтобы не соврать числом.
  const { correctCount, total: totalQ } = readQuizScore(result, quiz);
  const gap = result ? Math.max(0, quiz.pass_threshold - result.percent) : 0;
  // Во время разбора (result показан), после сдачи И в тупике «попытки
  // исчерпаны» форма НЕкликабельна. noAttemptsLeft раньше в этот флаг не входил:
  // варианты ответа оставались кликабельными, а кнопки «Проверить» уже не было —
  // ученик заполнял форму, которую физически нельзя отправить.
  const locked = passed || busy || !!result || noAttemptsLeft;

  return (
    <div className={'quiz-card' + (passed ? ' is-passed' : '')}>
      <div className="quiz-card__head">
        <div className="quiz-card__title"><Icon name="check-circle" size={18} /><h3 className="t-h4">{quiz.title || 'Тест'}</h3></div>
        {passed
          ? <Pill tone="ok" icon="check">Сдан · {quiz.best_percent || result?.percent}%</Pill>
          : <div className="quiz-meta">
              <span>Порог <span className="num">{quiz.pass_threshold}%</span></span>
              {quiz.max_attempts > 0 && <><span className="quiz-meta__sep">·</span><span>Попытки <span className="num">{attemptsUsed}/{quiz.max_attempts}</span></span></>}
            </div>}
      </div>
      {quiz.intro_md && <div className="quiz-intro md"><ChatMarkdown source={quiz.intro_md} /></div>}

      <div className="quiz-questions">
        {quiz.questions.map((q, i) => {
          const r = resById[q.id];
          return (
            <div key={q.id} className="quiz-q">
              <div className="quiz-q__head">
                <span className="quiz-q__num num">{i + 1}</span>
                <span className="quiz-q__text">{q.text}{q.qtype === 'multi' && <span className="quiz-q__hint"> · несколько вариантов</span>}</span>
                {r && <span className={'quiz-q__verdict quiz-q__verdict--' + (r.correct ? 'ok' : 'bad')}>
                  <Icon name={r.correct ? 'check-circle' : 'x-circle'} size={16} /></span>}
              </div>
              <div className={(q.qtype === 'single' || q.qtype === 'bool') ? 'opt-group' : 'opt-group'}
                role={q.qtype === 'multi' ? 'group' : 'radiogroup'}>
                {(q.qtype === 'single' || q.qtype === 'multi') && (q.options || []).map((o) => (
                  <OptBtn key={o.id} multi={q.qtype === 'multi'} disabled={locked}
                    selected={q.qtype === 'multi' ? (answers[q.id] || []).includes(o.id) : answers[q.id] === o.id}
                    verdict={optVerdict(q, o.id)}
                    onPick={() => q.qtype === 'multi' ? toggleMulti(q.id, o.id) : set(q.id, o.id)}>{o.text}</OptBtn>
                ))}
                {q.qtype === 'bool' && [true, false].map((v) => (
                  <OptBtn key={String(v)} disabled={locked} selected={answers[q.id] === v}
                    verdict={optVerdict(q, v)} onPick={() => set(q.id, v)}>{v ? 'Верно' : 'Неверно'}</OptBtn>
                ))}
                {q.qtype === 'text' && (
                  <input className={'input opt-text' + (r ? (r.correct ? ' is-correct' : ' is-incorrect') : '')}
                    disabled={locked} value={answers[q.id] || ''} placeholder="Ваш ответ"
                    onChange={(e) => set(q.id, e.target.value)} />
                )}
                {q.qtype === 'number' && (
                  <input className={'input opt-text' + (r ? (r.correct ? ' is-correct' : ' is-incorrect') : '')}
                    type="number" disabled={locked} value={answers[q.id] ?? ''} placeholder="Число"
                    onChange={(e) => set(q.id, e.target.value)} />
                )}
                {r && !r.correct && (q.qtype === 'text' || q.qtype === 'number') && correctAnswerText(q) && (
                  <div className="opt-answer"><Icon name="check-circle" size={13} />Правильный ответ: {correctAnswerText(q)}</div>
                )}
              </div>
              {r && r.explanation && (
                <div className="quiz-explain"><Icon name="info" size={14} /><span>{r.explanation}</span></div>
              )}
            </div>
          );
        })}
      </div>

      {result && (
        <div className={'quiz-result quiz-result--' + (result.passed ? 'pass' : 'fail')}>
          <div className="quiz-result__score">
            <div><div className="quiz-result__pct num">{result.percent}%</div>
              <div className="quiz-result__pct-sub num">{result.score}/{result.max_score}</div></div>
          </div>
          <div className="quiz-result__body">
            <div className="quiz-result__verdict">
              <Icon name={result.passed ? 'check-circle' : 'alert-circle'} size={16} />
              {result.passed ? 'Тест сдан' : 'Порог не достигнут'}
            </div>
            <div className="quiz-result__gap">
              {correctCount != null && (
                <>Верно <span className="num">{correctCount}</span> из <span className="num">{totalQ}</span>{result.passed ? ' — порог пройден' : ' · '}</>
              )}
              {!result.passed && <>до порога не хватило <span className="num">{gap}%</span></>}
              {correctCount == null && result.passed && 'Порог пройден'}
            </div>
            <div className="quiz-result__line">Порог сдачи — <span className="num">{quiz.pass_threshold}%</span>
              {quiz.max_attempts > 0 && <> · попытки <span className="num">{attemptsUsed}/{quiz.max_attempts}</span></>}</div>
          </div>
        </div>
      )}

      {!passed && !noAttemptsLeft && (
        <div className="quiz-actions">
          {result
            ? <Button variant="primary" iconLeft="refresh" onClick={retry}>Попробовать ещё раз</Button>
            : <Button variant="primary" loading={busy} onClick={submit}>Проверить</Button>}
          {result && <span className="t-caption text-mute">Исправьте ответы и проверьте снова</span>}
        </div>
      )}
      {/* ST-5: тупик «попытки исчерпаны» перестаёт быть надписью без выхода. */}
      {noAttemptsLeft && (
        <div className="quiz-blocked">
          <div className="quiz-blocked__head"><Icon name="lock" size={16} />
            Попытки закончились. Запросите пересдачу у куратора</div>
          <p className="quiz-blocked__body">
            Вы использовали все попытки (<span className="num">{attemptsUsed}</span> из <span className="num">{quiz.max_attempts}</span>).
            Сбросить их может куратор — после этого тест откроется заново.
          </p>
          <div><Button variant="primary" iconLeft="refresh" loading={retakeBusy}
            disabled={retakeAsked} onClick={requestRetake}>
            {retakeAsked ? 'Запрос отправлен куратору' : 'Запросить пересдачу'}</Button></div>
          {retakeAsked && (
            <p className="t-caption text-mute">
              Запрос отправлен куратору. Когда он сбросит попытки, обновите страницу — тест откроется заново.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── домашнее задание ────────────────────────────────────────────────────────
const DEC_TONE = { accepted: 'ok', revision: 'warn', rejected: 'bad' };
const DEC_LABEL = { accepted: 'Принято', revision: 'На доработку', rejected: 'Отклонено' };

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function HomeworkPane({ hw, onChanged, onDirty }) {
  const toast = useToast();
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const sub = hw.my_submission;
  // ST-15: набранный ответ и выбранные файлы живут только здесь — пока они есть
  // (или пока летит сдача), урок по возврату во вкладку не перечитываем.
  const dirty = homeworkIsDirty({ busy, body, files });
  useEffect(() => {
    if (!onDirty) return undefined;
    onDirty(dirty);
    return () => onDirty(false);
  }, [onDirty, dirty]);
  const accepted = sub && sub.status === 'accepted';
  const canSubmit = !sub || sub.status === 'revision' || sub.status === 'rejected';
  // ST-6: окно обратной связи по тарифу закрыто и проверяет куратор → бэк не
  // примет сдачу вообще (feedback_closed). Композер в этом случае не показываем:
  // кнопка, нажатие на которую гарантированно даёт ошибку, — худший вариант,
  // человек винит себя. Условие целиком — в lessonState.isFeedbackClosed.
  const feedbackBlocked = isFeedbackClosed(hw);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      await api.submitHomework(hw.id, body, files);
      toast.show('Задание отправлено на проверку', 'success');
      setBody(''); setFiles([]); onChanged && onChanged();
    } catch (e) { toast.show(academyErrorText(e, 'Не удалось отправить'), 'danger'); }
    finally { setBusy(false); }
  }, [hw, body, files, toast, onChanged]);

  return (
    <div className={'hw-card' + (accepted ? ' is-accepted' : '')}>
      <div className="hw-card__head">
        <div className="hw-card__title"><Icon name="edit" size={18} /><h3 className="t-h4">{hw.title || 'Домашнее задание'}</h3></div>
        {sub && <Pill tone={SUB_TONE[sub.status]} size="sm" glyph={false}>{SUB_LABEL[sub.status]}</Pill>}
      </div>
      {hw.instructions_md && <div className="hw-instructions md"><ChatMarkdown source={hw.instructions_md} /></div>}

      {sub && (
        <div className="hw-timeline">
          <div className="hw-step hw-step--done">
            <div className="hw-step__rail"><span className="hw-step__dot"><Icon name="check" size={11} /></span><span className="hw-step__line" /></div>
            <div className="hw-step__body">
              <div className="hw-step__title">Отправлено<span className="hw-submission__attempt">попытка {sub.attempt_no}</span></div>
              <div className="hw-step__time">{fmtDate(sub.created_at)}</div>
              {sub.body && <div className="hw-submission__body">{sub.body}</div>}
              {(sub.attachments || []).map((a) => (
                <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="hw-chip hw-chip--file">
                  <Icon name="file-text" size={13} /><span className="hw-chip__name">{a.name}</span></a>
              ))}
            </div>
          </div>
          {(sub.reviews || []).length === 0 ? (
            <div className={'hw-step ' + (sub.status === 'submitted' ? 'hw-step--active' : 'hw-step--pending')}>
              <div className="hw-step__rail"><span className="hw-step__dot"><Icon name="clock" size={11} /></span></div>
              <div className="hw-step__body"><div className="hw-step__title">{sub.status === 'submitted' ? 'На проверке у куратора' : 'Ожидает проверки'}</div></div>
            </div>
          ) : sub.reviews.map((rv, i) => (
            <div key={i} className={'hw-step hw-step--' + rv.decision}>
              <div className="hw-step__rail"><span className="hw-step__dot">
                <Icon name={rv.decision === 'accepted' ? 'check' : rv.decision === 'revision' ? 'refresh' : 'close'} size={11} /></span></div>
              <div className="hw-step__body">
                <div className="hw-step__title">{DEC_LABEL[rv.decision]}</div>
                <div className="hw-step__time">{fmtDate(rv.created_at)}</div>
                <div className={'hw-review hw-review--' + rv.decision}>
                  <div className="hw-review__head">
                    <Pill tone={DEC_TONE[rv.decision]} size="sm" glyph={false}>{DEC_LABEL[rv.decision]}</Pill>
                    {rv.score != null && <span className="hw-review__score num">{rv.score} б.</span>}
                  </div>
                  {rv.reviewer && <div className="hw-review__who"><Icon name="user" size={12} />{rv.reviewer}</div>}
                  {rv.comment && <div className="hw-review__comment">{rv.comment}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {canSubmit && feedbackBlocked && (
        <div className="hw-closed">
          <span className="hw-closed__glyph"><Icon name="clock" size={16} /></span>
          <div className="hw-closed__body">
            <div className="hw-closed__title">Период обратной связи по тарифу завершён — обратитесь к куратору</div>
            <div className="t-caption text-mute">
              По вашему тарифу срок проверки работ закончился, поэтому новые сдачи не принимаются.
              Если задание нужно закрыть, куратор или организатор обучения продлит обратную связь.
            </div>
          </div>
        </div>
      )}

      {canSubmit && !feedbackBlocked && (
        <div className="hw-composer">
          {sub && <div className={'hw-resubmit-note'}><Icon name="refresh" size={13} />Отправьте работу заново с учётом замечаний.</div>}
          <Field label="Ваш ответ">
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} minRows={4}
              placeholder="Опишите выполнение задания…" />
          </Field>
          {hw.allow_files && (
            <div className="hw-files">
              <label className="hw-files__pick">
                <Icon name="upload" size={16} /><span>Приложить файлы</span>
                <input type="file" multiple hidden onChange={(e) => setFiles([...e.target.files])} />
              </label>
              {hw.require_files && !files.length && <span className="hw-files__req hw-files__req--missing">Файл обязателен</span>}
              {files.length > 0 && (
                <div className="hw-chips">
                  {files.map((f, i) => (
                    <span key={i} className="hw-chip"><span className="hw-chip__name">{f.name}</span>
                      <button type="button" className="hw-chip__x" aria-label="Убрать"
                        onClick={() => setFiles(files.filter((_, j) => j !== i))}><Icon name="close" size={12} /></button></span>
                  ))}
                </div>
              )}
            </div>
          )}
          <div><Button variant="primary" loading={busy} onClick={submit}>{sub ? 'Отправить заново' : 'Отправить на проверку'}</Button></div>
          {!hw.blocks_progression && (
            <div className="t-caption text-mute"><Icon name="info" size={13} /> После отправки можно продолжать обучение — проверка куратора не задерживает.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── урок (правая колонка) ───────────────────────────────────────────────────
function LessonPane({ lessonId, frozen, subscriptionHold, onChanged, onForbidden, onProgram, onUpgrade }) {
  const [nonce, setNonce] = useState(0);
  const [step, setStep] = useState(null);
  // ST-15: сторож для refetch по возврату во вкладку. Незаконченный ввод живёт в
  // дочерних формах (тест, ДЗ) — LessonPane их состояния не видит, поэтому зоны
  // сообщают о себе сами. Признаки — в ref, а не в state: сторож обязан быть
  // стабильным по ссылке (иначе подписка на фокус пересоздавалась бы каждый
  // рендер), а перерисовывать урок из-за отметки «грязно» незачем.
  const dirtyRef = useRef({ quiz: false, homework: false });
  const markDirty = useCallback((zone, value) => { dirtyRef.current[zone] = !!value; }, []);
  const onQuizDirty = useCallback((v) => markDirty('quiz', v), [markDirty]);
  const onHomeworkDirty = useCallback((v) => markDirty('homework', v), [markDirty]);
  const canRefetch = useCallback(() => lessonRefetchAllowed(dirtyRef.current), []);
  // Дерево уже знает о заморозке — не дёргаем урок, чтобы не получить ожидаемый 403.
  // ST-15: вердикт куратора по ДЗ обязан появляться без ручной перезагрузки —
  // ученик держит урок открытым, уходит в другую вкладку и возвращается. На
  // замороженном курсе подписки нет: там уроки и так не запрашиваются.
  const q = useFakeQuery('academy-lesson-' + lessonId + '-' + nonce,
    () => (frozen ? Promise.resolve(null) : api.getLesson(lessonId)), [lessonId, nonce, frozen],
    { refetchOnFocus: !frozen, canRefetch });
  const lesson = q.data;
  // Гонка: дерево ещё не знает о блокировке, а урок уже отдал 403. Причину берём
  // ИЗ КОДА ответа (useFakeQuery теперь его довозит), а не гадаем: 403 бывает и
  // locked_sequential, и locked_drip, и locked_tariff, и locked_frozen — раньше
  // все они схлопывались в «Доступ заморожен».
  const denied = !frozen ? lessonAccessNotice(q.error) : null;
  const deniedCode = denied ? denied.code : null;

  useEffect(() => {
    if (denied) onForbidden && onForbidden(denied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deniedCode]);

  useEffect(() => {
    if (frozen || !lesson) return;
    api.markViewed(lessonId).then(() => onChanged && onChanged()).catch(() => {});
    // eslint-disable-next-line
  }, [lessonId, lesson?.id, frozen]);

  // B6: хартбит времени просмотра. Пока урок открыт, не заморожен/не закрыт и вкладка
  // видима — раз в ~30с шлём ПРИРАЩЕНИЕ секунд с прошлого успешного пинга. Финальный
  // пинг — при смене урока/анмаунте/уходе из вкладки (visibility→hidden). Не считаем
  // время, пока вкладка скрыта (lastPingAt сбрасывается на момент возврата). Best-effort:
  // ошибки глотаем, без UI; не дважды-считаем (lastPingAt-ориентир). LessonPane монтируется
  // с key={lessonId}, поэтому смена урока = размонтирование → cleanup отправит финальный пинг.
  const heartbeatActive = !frozen && !denied && !!lesson;
  const lastPingAtRef = useRef(0);
  useEffect(() => {
    if (!heartbeatActive) return undefined;
    let timer = null;
    // Приращение секунд с прошлой отсечки (lastPingAt), клампим клиентски [0..MAX].
    const elapsed = () => {
      const now = Date.now();
      const last = lastPingAtRef.current || now;
      return Math.min(HEARTBEAT_MAX_DELTA, Math.floor((now - last) / 1000));
    };
    const ping = () => {
      const d = elapsed();
      lastPingAtRef.current = Date.now();
      if (d >= 1) api.heartbeat(lessonId, d).catch(() => {});
    };
    const start = () => {
      lastPingAtRef.current = Date.now();
      if (!timer) timer = setInterval(ping, HEARTBEAT_INTERVAL_MS);
    };
    const stop = (flush) => {
      if (timer) { clearInterval(timer); timer = null; }
      if (flush) {
        const d = elapsed();
        lastPingAtRef.current = Date.now();
        if (d >= 1) api.heartbeat(lessonId, d).catch(() => {});
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop(true);   // ушли из вкладки — финальный пинг накопленного и пауза счёта
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop(true);   // смена урока / анмаунт — финальный пинг остатка
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heartbeatActive, lessonId]);

  // ST-15: вердикт приехал перечиткой по фокусу — значит, он изменился и в ДЕРЕВЕ
  // (бейдж ДЗ в рельсе программы, прогресс курса). Иначе на десктопе, где рельс
  // виден рядом, ученик читал бы «Принято» в таймлайне и «На проверке» в рельсе
  // одновременно. Первый переход (нет статуса → есть) не считаем: он приходит
  // вместе с первой загрузкой урока или сразу после сдачи, а там дерево уже
  // перечитывает HomeworkPane через onChanged.
  const hwStatus = (lesson && lesson.homework && lesson.homework.my_submission)
    ? lesson.homework.my_submission.status : null;
  const prevHwStatusRef = useRef(hwStatus);
  useEffect(() => {
    const prev = prevHwStatusRef.current;
    prevHwStatusRef.current = hwStatus;
    if (prev !== null && hwStatus !== prev) onChanged && onChanged();
  }, [hwStatus, onChanged]);

  // N1 (живое QA, 380 px). У урока, где есть И материал, И тест, переключатель
  // «Материал｜Тест» стартовал в полосе липкого футера: на узком экране шапка
  // прогресса курса (или плашка «Курс пройден»), кнопка «Программа курса» и
  // заголовок урока с подзаголовком съедают почти весь экран, и переключатель
  // оказывается ниже — под панелью, которая всегда сверху. Ученик его не находил.
  // Не подгоняем отступы «на глаз» (высота шапки зависит от длины названий):
  // просим браузер вывести переключатель в видимую зону минимальной прокруткой.
  // Где кончается видимая зона, сказано в CSS через scroll-margin (.cp-steps,
  // academy.css): снизу её режет футер измеренной высоты, сверху — липкая шапка.
  // block:'nearest' → там, где переключатель и так виден (1740), прокрутка не
  // двигается вовсе. Зависимость только от id урока: на перечитке по фокусу
  // (ST-15) и на переключении шага прокрутка не прыгает.
  const stepsRef = useRef(null);
  useEffect(() => {
    const el = stepsRef.current;
    if (!lesson || !el || typeof el.scrollIntoView !== 'function') return;
    el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson && lesson.id]);

  const reload = useCallback(() => { setNonce((n) => n + 1); onChanged && onChanged(); }, [onChanged]);

  // #6: заморожено (флаг дерева или 403 locked_frozen на самом уроке) — тело
  // урока не рендерим, показываем экран «Доступ заморожен». Любая ДРУГАЯ
  // причина отказа получает свой экран и свой текст.
  // Порядок причин — ЗЕРКАЛО services.lesson_lock: «(0) Заморозка перебивает
  // все прочие коды», и только «(0.5)» — пауза подписки. Ставить паузу первой
  // нельзя: замороженному организатором ученику экран назвал бы причиной
  // подписку, а бэк на том же уроке отдаёт код 'frozen'.
  // Сегодня сюда доходит только `denied`-ветка (корневой рендер перехватывает и
  // заморозку, и паузу раньше), но порядок держим верным именно потому, что
  // мёртвую ветку не проверяет ни один тест: перестановка корневых условий
  // сделала бы ошибку живой и молчаливой.
  if (frozen || (denied && denied.frozen)) return <FrozenNotice />;
  if (subscriptionHold || (denied && denied.code === 'locked_subscription')) {
    return <SubscriptionHoldNotice />;
  }
  if (denied) {
    return <LessonLockedNotice notice={denied} onProgram={onProgram}
      onUpgrade={deniedCode === 'locked_tariff' ? onUpgrade : null} />;
  }

  // Тест считаем настоящим, только если в нём есть вопросы (бэкенд вырожденный тест
  // ученику и так не отдаёт, но защищаемся от dead-end «Проверить → 0/0 → провал»).
  const realQuiz = lesson && lesson.quiz && (lesson.quiz.questions || []).length > 0
    ? lesson.quiz : null;
  // #7: урок может иметь и тест (с вопросами), и ДЗ одновременно — это ДВА
  // независимых шага, оба должны быть видимы/доступны. Порядок: Материал → Тест →
  // Задание. Условия независимы (не if/else), поэтому при наличии обоих оба
  // попадают в steps; одиночные тест/ДЗ продолжают работать как прежде.
  const steps = [];
  if (lesson) {
    const hasMaterial = !!(lesson.article_md || lesson.video_url || (lesson.materials || []).length);
    const hasQuiz = !!realQuiz;
    const hasHomework = !!lesson.homework;
    if (hasMaterial) steps.push({ key: 'material', label: 'Материал', icon: lesson.video_url ? 'camera' : 'book' });
    if (hasQuiz) steps.push({ key: 'quiz', label: 'Тест', icon: 'check' });
    if (hasHomework) steps.push({ key: 'homework', label: 'Задание', icon: 'edit' });
    if (!steps.length) steps.push({ key: 'material', label: 'Материал', icon: 'book' });
  }
  const doneMap = lesson ? {
    material: lesson.progress !== 'not_started',
    quiz: !!(realQuiz && realQuiz.passed),
    homework: !!(lesson.homework && lesson.homework.my_submission
      && lesson.homework.my_submission.status === 'accepted'),
  } : {};
  const active = (step && steps.some((s) => s.key === step)) ? step : (steps[0] ? steps[0].key : 'material');
  const activeIdx = steps.findIndex((s) => s.key === active);
  const nextStep = activeIdx >= 0 && activeIdx < steps.length - 1 ? steps[activeIdx + 1] : null;
  const nextLabel = nextStep ? (nextStep.key === 'quiz' ? 'Пройти тест' : nextStep.key === 'homework' ? 'К заданию' : 'Далее') : '';

  return (
    <QueryBoundary loading={q.loading && !lesson} error={q.error} onRetry={reload}>
      {lesson && (
        <article className="cp-article">
          <header className="cp-article__head">
            <span className="cp-article__type t-eyebrow"><Icon name={steps[0] ? steps[0].icon : 'book'} size={13} />Урок</span>
            <h1 className="t-h2">{lesson.title}</h1>
            {lesson.summary && <p className="text-dim">{lesson.summary}</p>}
          </header>

          {steps.length > 1 && (
            <div className="cp-steps" role="tablist" aria-label="Темы урока" ref={stepsRef}>
              {steps.map((s, i) => (
                <React.Fragment key={s.key}>
                  {i > 0 && <span className={'cp-step__sep' + (doneMap[steps[i - 1].key] ? ' is-done' : '')} aria-hidden="true" />}
                  <button type="button" role="tab" aria-selected={active === s.key}
                    className={'cp-step' + (active === s.key ? ' is-active' : '') + (doneMap[s.key] ? ' is-done' : '')}
                    onClick={() => setStep(s.key)}>
                    <span className="cp-step__num">{doneMap[s.key] ? <Icon name="check" size={13} /> : (i + 1)}</span>
                    {s.label}
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}

          {active === 'material' && (
            <>
              {/* Видео урока — платный контент. Полностью запретить копирование
                  нельзя (байты доходят до браузера; настоящий запрет — это DRM),
                  но одноклоковые пути закрываем: `nodownload` убирает кнопку
                  скачивания из штатных контролов, отключённый PiP и remote
                  playback — вынос потока в отдельное окно/на устройство, а
                  подавленное контекстное меню — «Сохранить видео как…». Сервер
                  дополнительно не отдаёт этот адрес при открытии его страницей
                  (common/protected_media.py). */}
              {lesson.video_url && (
                <div className="cp-video">
                  {/* poster — кадр-заставка до нажатия «play». Без него
                      preload="metadata" даёт либо чёрный прямоугольник, либо
                      первый кадр, а у лекции это обычно пустая титульная
                      заставка. undefined, а не '': пустая строка в poster
                      заставляет браузер грузить САМУ СТРАНИЦУ как картинку. */}
                  <video controls preload="metadata" src={lesson.video_url}
                    poster={lesson.video_poster_url || undefined}
                    controlsList="nodownload noremoteplayback noplaybackrate"
                    disablePictureInPicture
                    onContextMenu={(e) => e.preventDefault()} />
                </div>
              )}
              {lesson.article_md && <div className="ac-prose"><ChatMarkdown source={lesson.article_md} /></div>}
              {(lesson.materials || []).length > 0 && (
                <div className="cp-materials">
                  <span className="t-eyebrow">Материалы</span>
                  {lesson.materials.map((m) => (
                    <a key={m.id} href={m.url} target="_blank" rel="noreferrer" className="cp-material-chip">
                      <Icon name="file-text" size={16} /><span className="cp-material-chip__name">{m.name}</span>
                      <Icon name="download" size={15} className="cp-material-chip__dl" />
                    </a>
                  ))}
                </div>
              )}
              {nextStep && (
                <div className="cp-step-next">
                  <Button variant="primary" iconRight="arrow-right" onClick={() => setStep(nextStep.key)}>{nextLabel}</Button>
                </div>
              )}
            </>
          )}

          {active === 'quiz' && realQuiz && (
            <>
              <QuizRunner quiz={realQuiz} onPassed={reload} onDirty={onQuizDirty} />
              {nextStep && realQuiz.passed && (
                <div className="cp-step-next">
                  <Button variant="primary" iconRight="arrow-right" onClick={() => setStep(nextStep.key)}>{nextLabel}</Button>
                </div>
              )}
            </>
          )}

          {active === 'homework' && lesson.homework
            && <HomeworkPane hw={lesson.homework} onChanged={reload} onDirty={onHomeworkDirty} />}
        </article>
      )}
    </QueryBoundary>
  );
}

// ── плеер ───────────────────────────────────────────────────────────────────
function CoursePlayer() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  // Куда вести с экрана паузы. Правило — то же, что у апселла Аналитики
  // (PlanLockedPanel): подписку продлевает владелец/админ пространства, а
  // сотруднику, которому курс достался охватом «вся команда», кнопка в чужой
  // кабинет бесполезна. Нет membership-данных — кнопку показываем: спрятать её
  // у владельца хуже, чем показать тому, кто упрётся в 403 на оплате.
  const { activeWorkspace } = useAuth() || {};
  const _m = activeWorkspace && activeWorkspace.membership;
  const canManagePlan = !_m || !!(_m.is_admin || _m.is_owner);
  const renewPlanTarget = tariffTarget(activeWorkspace);
  const onRenewPlan = canManagePlan ? () => navigate(renewPlanTarget) : null;
  const { isMobile } = useBp();
  const toast = useToast();
  const [treeNonce, setTreeNonce] = useState(0);
  const [railOpen, setRailOpen] = useState(false);
  const [buy, setBuy] = useState(null);   // { course, tariffs } — открытое оформление
  const treeQ = useFakeQuery('academy-tree-' + id + '-' + treeNonce, () => api.getCourseTree(id), [id, treeNonce]);
  const tree = treeQ.data;
  const lessonId = params.get('lesson') ? Number(params.get('lesson')) : null;

  const reloadTree = useCallback(() => setTreeNonce((n) => n + 1), []);

  // N1 (живое QA, 380 px): открытый урок живёт в ?lesson=, а роутерный
  // setSearchParams ВСЕГДА просит preventScrollReset (lib/router.jsx:255) — иначе
  // он ронял бы прокрутку на каждой правке query. Побочный эффект: смена урока
  // прокрутку НЕ сбрасывала, и новый урок открывался на смещении предыдущего.
  // На 380 px читаемая колонка почти равна экрану: ученик, нажавший «Далее» внизу
  // длинного урока (или вернувшийся к пройденному через «Программу курса»), видел
  // середину нового урока — переключателя «Материал｜Тест» на экране не было
  // вовсе, а низ занимал липкий футер. Для ученика смена урока — переход, а не
  // прокрутка: возвращаем начало урока, как это делает роутер на обычном переходе.
  useEffect(() => {
    if (!lessonId) return;
    const main = document.querySelector('[data-scroll-main]');
    if (main) main.scrollTop = 0;
  }, [lessonId]);
  // Новую строку запроса считаем от ЖИВОГО адреса (liveSearch), а не от снимка
  // `prev`: снимок в history-шиме принадлежит рендеру, в котором создан колбэк,
  // и второй писатель одного коммита затирал работу первого (см. playerParams.js).
  const pick = useCallback((lid, opts) => {
    setParams(withLesson(liveSearch(), lid), { replace: !!(opts && opts.replace) });
    setRailOpen(false);
  }, [setParams]);

  // плоский список уроков + прогресс
  const flat = (tree?.modules || []).flatMap((m) => m.lessons);
  const required = flat.filter((l) => l.is_required);
  const doneReq = required.filter((l) => l.progress === 'completed').length;
  const totalReq = required.length;
  const pct = tree?.enrollment ? tree.enrollment.progress_pct : (totalReq ? Math.round(doneReq * 100 / totalReq) : 0);
  const idx = flat.findIndex((l) => l.id === lessonId);
  const prev = idx > 0 ? flat[idx - 1] : null;
  const next = idx >= 0 && idx < flat.length - 1 ? flat[idx + 1] : null;
  const firstOpen = flat.find((l) => !l.lock);
  const curNode = flat.find((l) => l.id === lessonId);
  // Открытый урок закрыт замком (последовательность/расписание/тариф). Признак
  // берём из дерева — того же источника, по которому рельс рисует замки, поэтому
  // он готов ещё до 403 от самого урока.
  const curLocked = !!(curNode && curNode.lock);

  // Урок вернул 403 — перечитываем дерево, чтобы рельс и верхнеуровневые флаги
  // стали авторитетными. Тост показываем ТОЛЬКО про заморозку: это состояние
  // всего курса, о нём ученик мог не знать. Про локи урока (последовательность,
  // расписание, тариф) уже говорит сам экран — второй раз кричать незачем.
  const onLessonForbidden = useCallback((notice) => {
    if (notice && notice.frozen) toast.show('Доступ к курсу временно заморожен', 'warning');
    reloadTree();
  }, [toast, reloadTree]);

  const enrolled = tree && tree.enrollment != null;
  const completed = enrolled && pct >= 100;
  const cert = tree && tree.enrollment && tree.enrollment.certificate;
  // #6: запись на паузе — доступ к урокам закрыт, показываем экран «заморожено».
  const frozen = !!(tree && tree.frozen);
  const subscriptionHold = !!(tree && tree.subscription_hold);

  // ST-10: вход в плеер без ?lesson= давал экран «Выберите урок» — лишний шаг на
  // пути к уроку и с карточки «Моих курсов», и по прямой ссылке на курс. Открываем
  // тот урок, с которого ученик продолжает. replace: этот промежуточный адрес не
  // должен оставаться в истории — иначе «назад» возвращало бы на него же.
  // `!subscriptionHold` — по той же причине, что и `!frozen`: на паузе подписки
  // lesson_lock запирает КАЖДЫЙ урок, nextLessonIdFromTree вернёт null, и
  // авто-открытие всё равно не сработает. Но условие обязано быть явным: иначе
  // при любом будущем «первый урок всегда открыт» плеер молча увёл бы
  // приостановленного в урок, который тут же ответит 403.
  const resumeLessonId = (tree && enrolled && !frozen && !subscriptionHold && !lessonId)
    ? nextLessonIdFromTree(tree) : null;
  useEffect(() => {
    if (resumeLessonId) pick(resumeLessonId, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeLessonId]);

  // Барьер «купить/записаться» переехал на лендинг. Не записан → уводим на
  // лендинг курса (/course/:slug); там CTA по состоянию (записаться/купить).
  // Записанный ученик заходит прямо в плеер, как раньше. Если slug по какой-то
  // причине нет — деградируем до прежнего in-player барьера (fallback ниже).
  //
  // ВАЖНО (W3-player r2): при возврате с оплаты НЕ редиректим на лендинг до тех
  // пор, пока ПЕРЕЧИТАННОЕ дерево не покажет запись (enrollment != null). Иначе
  // только что заплативший покупатель отскакивал бы на лендинг: на успешном
  // поллинге мы чистили ?purchase= и дёргали reloadTree в одном батче, а
  // useFakeQuery держит СТАРОЕ (не записан) дерево, пока летит refetch — и на
  // один рендер shouldRedirect становился true (param уже снят, дерево ещё
  // старое). Поэтому: (а) флаг awaitingEnrollment держит редирект выключенным
  // всё post-purchase-окно, (б) редирект не срабатывает, пока дерево грузится/
  // перечитывается, (в) флаг и ?purchase= снимаются только когда свежее дерево
  // подтвердило запись (или поллинг исчерпан, см. ниже).
  const treeSlug = tree && tree.slug;
  const pendingPurchase = !!params.get('purchase');
  // Ожидаем применения записи после возврата с оплаты — стартует при входе с
  // ?purchase= и держится, пока перечитанное дерево не покажет enrolled.
  const [awaitingEnrollment, setAwaitingEnrollment] = useState(false);
  const treeLoading = treeQ.loading;

  // ST-3: дерево отдаёт enrollment:null И при истёкшем доступе, и когда ученик
  // никогда не записывался — различить их можно только по /my/ (там истёкшая
  // запись видна вместе с expires_at). Запрос уходит ТОЛЬКО когда записи в
  // дереве нет, и до его ответа редирект на лендинг подавлен: иначе истёкший
  // ученик снова улетал бы на лендинг, а лендинг — обратно сюда.
  //
  // ГОНКА (замер на деве: редирект на 153 мс, ответ /my/ на 160 мс). Здесь нужен
  // не «запрос грузится», а явный признак «ответ по ЭТОМУ курсу и ЭТОМУ
  // поколению дерева получен»: на рендере, где notEnrolled впервые становится
  // true, состояние любого хука-запроса ещё принадлежит предыдущему ключу
  // (loading=false, data=null) — и navigate успевал уйти в том же коммите.
  // Поэтому храним ключ вместе с ответом и сравниваем его с запрошенным.
  const notEnrolled = !!tree && tree.enrollment == null;
  const probeKey = notEnrolled ? `${id}:${treeNonce}` : null;
  const [myProbe, setMyProbe] = useState({ key: null, list: null });
  useEffect(() => {
    if (!probeKey) return undefined;
    let alive = true;
    // Ошибку /my/ считаем «ответом без записи»: иначе плеер завис бы на
    // плейсхолдере навсегда, а барьер покупки живёт на лендинге.
    api.getMyCourses()
      .then((list) => { if (alive) setMyProbe({ key: probeKey, list }); })
      .catch(() => { if (alive) setMyProbe({ key: probeKey, list: null }); });
    return () => { alive = false; };
  }, [probeKey]);
  const myResolved = !!probeKey && myProbe.key === probeKey;
  const myEnrollment = myResolved ? findMyEnrollment(myProbe.list, id) : null;

  // Единая точка решения (чистая функция, покрыта тестом access.test.js):
  // 'player' | 'probing' | 'expired' | 'redirect'.
  const view = playerAccessView({
    hasTree: !!tree, enrolled, slug: treeSlug, treeLoading,
    pendingPurchase, awaitingEnrollment, myResolved, myEnrollment,
  });
  const accessExpired = view === 'expired';
  const shouldRedirect = view === 'redirect';
  useEffect(() => {
    if (shouldRedirect) navigate('/course/' + treeSlug, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRedirect, treeSlug]);
  // Пока редирект применяется и пока выясняем, не истёк ли доступ, — нейтральный
  // плейсхолдер вместо мигающего in-player барьера.
  const showPlaceholder = shouldRedirect || view === 'probing';

  // Тост печатается в общий портал (fixed, правый нижний угол) и на 380 px
  // ложился ровно на sticky-футер урока: «Далее» становилась некликабельной
  // именно в тот момент, когда ученик её ищет (после сдачи теста и после отправки
  // ДЗ). Поднимаем стек тостов на высоту футера, пока футер на экране: класс на
  // <body> + измеренная высота в переменной (правило — в styles/academy.css).
  const footerRef = useRef(null);
  // Условие ровно то же, при котором футер рендерится ниже (иначе класс мог бы
  // пережить уход на экран заморозки/истечения и держать смещение без футера).
  const footerShown = !!lessonId && enrolled && !curLocked && !frozen && !accessExpired && !showPlaceholder;
  useEffect(() => {
    const el = footerRef.current;
    if (!footerShown || !el) return undefined;
    const body = document.body;
    const apply = () => body.style.setProperty('--acad-lesson-footer-h', el.offsetHeight + 'px');
    body.classList.add('acad-lesson-footer');
    apply();
    // Высота футера меняется на переносе строки (xs) и при смене подписи середины.
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(apply); ro.observe(el); }
    return () => {
      if (ro) ro.disconnect();
      body.classList.remove('acad-lesson-footer');
      body.style.removeProperty('--acad-lesson-footer-h');
    };
  }, [footerShown]);

  const copyCertLink = useCallback(async () => {
    if (!cert) return;
    const url = `${window.location.origin}/certificate/${cert.public_id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.show('Ссылка на сертификат скопирована', 'success');
    } catch { toast.show(url, 'info'); }
  }, [cert, toast]);

  // Оформление всегда через BuyDialog (и с тарифами, и без): бэк требует согласия
  // на каждом чекауте, прямой вызов отсюда возвращал 400 consent_required.
  const buyCourse = useCallback(async () => {
    try {
      const tariffs = await api.getCourseTariffs(id);
      setBuy({ course: { id, title: tree?.title || '', price_rub: tree?.price_rub }, tariffs: tariffs || [] });
    } catch (e) { toast.show(academyErrorText(e, 'Не удалось открыть оформление'), 'danger'); }
  }, [id, toast, tree]);

  // ST-3: продление истёкшего доступа. Платный курс — та же единая форма покупки
  // (согласия собираются на каждом чекауте); бесплатный с ограниченным сроком —
  // повторная запись, которая на бэке продлевает срок.
  const [renewBusy, setRenewBusy] = useState(false);
  const renewAccess = useCallback(async () => {
    if (tree && tree.is_paid) { await buyCourse(); return; }
    setRenewBusy(true);
    try {
      await api.enroll(id);
      toast.show('Доступ к курсу возобновлён', 'success');
      reloadTree();
    } catch (e) { toast.show(academyErrorText(e, 'Не удалось продлить доступ'), 'danger'); }
    finally { setRenewBusy(false); }
  }, [tree, buyCourse, id, toast, reloadTree]);

  // Возврат из оплаты: ?purchase=<invoice> [&failed=1] — поллим статус, пока не
  // откроется доступ, затем перечитываем дерево.
  const purchaseInvoice = params.get('purchase');
  const purchaseFailed = params.get('failed');
  // БЛОКЕР (второй круг QA): чистка ?purchase= стирала уже выбранный урок.
  // Resume-эффект дописывал `&lesson=<id>`, а этот сторож в том же коммите
  // пересобирал адрес из снимка ДО него — и заплативший ученик получал пустой
  // «Выберите урок» ровно в тот момент, когда отдал деньги. Считаем от живого
  // адреса; replace — чтобы «назад» не возвращал на ?purchase= и не запускал
  // поллинг оплаты заново.
  const clearPurchaseParam = useCallback(() => {
    setParams(withoutPurchase(liveSearch()), { replace: true });
  }, [setParams]);
  // W3-player r2: на УСПЕШНОМ поллинге НЕ чистим ?purchase= сразу — иначе на
  // тот же рендер (со старым деревом) откроется редирект на лендинг. Только
  // взводим awaitingEnrollment и перечитываем дерево; параметр и флаг снимет
  // эффект ниже, когда свежее дерево покажет enrolled.
  const purchaseSucceededRef = useRef(false);
  useEffect(() => {
    if (!purchaseInvoice) return undefined;
    // ?failed=1 ставит браузерный FailURL Робокассы — это ДОГАДКА, а не факт:
    // при оплате через SberPay/СБП возврат из банковского приложения приходит
    // как отказ, а подписанный ResultURL об успехе — секундами позже. Раньше
    // этот параметр гасил поллинг целиком, до первого запроса, и покупатель с
    // прошедшей оплатой видел «Оплата не завершена». Теперь он лишь меняет
    // тон сообщения, а статус мы всё равно идём проверять.
    if (purchaseFailed) {
      toast.show('Проверяем оплату — не платите повторно', 'warning');
    }
    // Возврат с оплаты начался — держим редирект на лендинг выключенным всё окно.
    setAwaitingEnrollment(true);
    purchaseSucceededRef.current = false;
    let tries = 0, stopped = false, timer = null;
    const tick = async () => {
      if (stopped) return;
      try {
        const st = await api.getPurchaseStatus(purchaseInvoice);
        if (st.enrolled) {
          // Доступ выдан на бэке — перечитываем дерево; ?purchase= и
          // awaitingEnrollment снимет эффект-страж, когда дерево это подтвердит.
          purchaseSucceededRef.current = true;
          toast.show('Доступ к курсу открыт', 'success'); reloadTree(); return;
        }
        // Отказ объявляем только ОКОНЧАТЕЛЬНЫЙ: `is_provisional` поднят, пока
        // статус может отменить настоящий ResultURL (правило считает бэкенд,
        // см. purchasing.is_resurrectable_guess) — до тех пор продолжаем ждать.
        if (st.status === 'failed' && !st.is_provisional) {
          toast.show(st.failure_reason || 'Оплата не прошла', 'danger');
          setAwaitingEnrollment(false); clearPurchaseParam(); return;
        }
      } catch { /* транзиентно — продолжим поллинг */ }
      if (++tries < 15 && !stopped) { timer = setTimeout(tick, 2000); return; }
      if (!stopped) {
        // Поллинг исчерпан (~30с), доступ ещё не применился (медленный колбэк
        // шлюза). Не оставляем тихо старый барьер «не записан»: подсказываем
        // обновить и делаем финальную перечитку дерева. Барьер на лендинг при
        // этом не отскочит — awaitingEnrollment ещё взведён, а после перечитки
        // эффект-страж снимет его, если запись так и не пришла, либо чисто
        // закроет окно, если пришла.
        toast.show('Оплата ещё обрабатывается — обновите страницу, если доступ не открылся', 'warning');
        reloadTree();
      }
    };
    tick();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseInvoice]);

  // Эффект-страж W3-player r2: как только ПЕРЕЧИТАННОЕ дерево подтвердило запись
  // (enrolled, дерево не грузится) — снимаем awaitingEnrollment и чистим
  // ?purchase=. До этого момента редирект на лендинг подавлен. Срабатывает и на
  // нормальном успехе (st.enrolled → reloadTree), и на финальной перечитке
  // после исчерпания поллинга, если доступ к тому времени применился.
  useEffect(() => {
    if (!awaitingEnrollment) return;
    if (treeLoading) return;          // refetch ещё в полёте — ждём свежее дерево
    if (enrolled) {                   // свежее дерево показывает запись — окно закрыто
      setAwaitingEnrollment(false);
      if (params.get('purchase')) clearPurchaseParam();
      return;
    }
    // Дерево перечитано, но записи всё ещё нет. Если поллинг успел отдать
    // enrolled, дерево вот-вот станет enrolled — продолжаем ждать. Иначе
    // (поллинг исчерпан без успеха) снимаем флаг: редирект не сработает, пока
    // висит ?purchase=, но пользователь уже видит подсказку «обновите страницу».
    if (!purchaseSucceededRef.current && !purchaseInvoice) setAwaitingEnrollment(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingEnrollment, treeLoading, enrolled]);

  return (
    <div className="section-page">
      <PageHeader breadcrumbs={[{ label: 'Обучение', to: '/learning' }, { label: tree?.title || 'Курс' }]}
        title={tree?.title || 'Курс'} />
      <div className="section-body">
        <QueryBoundary loading={treeQ.loading && !tree} error={treeQ.error} onRetry={reloadTree}>
          {tree && accessExpired ? (
            /* ST-3: доступ закончился. Ни редиректа, ни молчания — состояние
               названо, дата показана, продление начинается здесь же. */
            <AccessExpiredNotice expiresAt={myEnrollment.expires_at}
              onRenew={renewAccess} busy={renewBusy} />
          ) : tree && showPlaceholder ? (
            /* Не записан: барьер «купить/записаться» живёт на лендинге — уводим туда
               (effect выше). Пока навигация применяется — и пока выясняем, не
               истёк ли доступ (ST-3), — показываем нейтральный плейсхолдер, а не
               старый in-player барьер: иначе он мигал бы на долю секунды. */
            (shouldRedirect
              ? <EmptyState icon="book" title="Открываем страницу курса…"
                  body="Перенаправляем на страницу курса, чтобы записаться или купить доступ." />
              /* Пока идёт проверка (ответ /my/ ещё в пути), обещать редирект
                 нельзя: доступ может оказаться истёкшим, и никуда мы не уйдём. */
              : <EmptyState icon="clock" title="Проверяем доступ к курсу…"
                  body="Секунду — уточняем, действует ли ваш доступ." />)
          ) : tree && frozen ? (
            /* #6: доступ заморожен — уроки недоступны, тела уроков не рендерим.
               Рельс оставляем видимым (все уроки помечены lock='frozen' и
               некликабельны), а в области контента — экран «Доступ заморожен». */
            <>
              <FrozenNotice />
              <div className="cp-layout">
                <Rail tree={tree} activeId={lessonId} onPick={pick} />
              </div>
            </>
          ) : tree && subscriptionHold ? (
            /* Пауза подписки — ПОСЛЕ заморозки: приоритет зеркалит lesson_lock
               (заморозка организатора перебивает паузу подписки), иначе бэк и
               фронт назовут одному ученику разные причины одного замка.

               Ветка корневая, а не только внутри LessonPane: у приостановленного
               next_lesson_id всегда null, ?lesson= в адресе нет, resume-подстановка
               выключена — до LessonPane он просто не доходил и упирался в
               «Выберите урок» с целиком отключённым рельсом. Экран без единой
               живой кнопки и без причины — это и есть та немота, ради которой
               признак вводился. */
            <>
              <SubscriptionHoldNotice onRenew={onRenewPlan} />
              <div className="cp-layout">
                <Rail tree={tree} activeId={lessonId} onPick={pick} />
              </div>
            </>
          ) : tree && (
            <>
              {/* шапка: запись / завершение / прогресс */}
              {!enrolled && tree.can_self_enroll ? (
                /* Fallback (нет slug для редиректа): прежний in-player барьер. */
                <div className="cp-enroll">
                  <div className="cp-enroll__text">
                    <span className="t-label">Начните курс, чтобы проходить уроки</span>
                    <span className="t-caption">{flat.length} {api.lessonsWord(flat.length)}{tree.issues_certificate ? ' · сертификат' : ''}</span>
                  </div>
                  <Button variant="primary" onClick={async () => {
                    try { await api.enroll(id); reloadTree(); }
                    catch (e) { toast.show(academyErrorText(e, 'Не удалось начать курс'), 'danger'); }
                  }}>Начать</Button>
                </div>
              ) : !enrolled && tree.can_purchase ? (
                /* Fallback (нет slug для редиректа): прежний in-player барьер покупки. */
                <div className="cp-enroll cp-enroll--paid">
                  <span className="cp-enroll__price"><Icon name="sparkles" size={18} /></span>
                  <div className="cp-enroll__text">
                    <span className="t-label">Платный курс · {money(tree.price_rub)}</span>
                    <span className="t-caption">{flat.length} уроков{tree.issues_certificate ? ' · сертификат' : ''} · постоянный доступ после покупки</span>
                  </div>
                  <Button variant="primary" onClick={buyCourse}>Купить · {money(tree.price_rub)}</Button>
                </div>
              ) : completed ? (
                <div className="cp-cert">
                  <span className="cp-cert__icon"><Icon name="sparkles" size={20} /></span>
                  <div className="cp-cert__text">
                    <div className="t-h4">Курс пройден!</div>
                    <div className="t-caption">
                      {cert
                        ? <>Поздравляем — ваш сертификат готов{cert.serial ? <> · № <span className="num">{cert.serial}</span></> : ''}.</>
                        : 'Поздравляем — вы освоили все обязательные уроки.'}
                    </div>
                  </div>
                  {cert && (
                    <div className="cp-cert__actions">
                      <Button variant="primary" size="sm" iconLeft="sparkles"
                        onClick={() => window.open(`/certificate/${cert.public_id}`, '_blank', 'noopener')}>
                        Открыть сертификат
                      </Button>
                      <Button variant="secondary" size="sm" iconLeft="copy" onClick={copyCertLink}>
                        Скопировать ссылку
                      </Button>
                    </div>
                  )}
                </div>
              ) : enrolled ? (
                <div className="cp-progress" role="group" aria-label="Прогресс курса">
                  <div className={'cp-ring' + (pct >= 100 ? ' cp-ring--ok' : '')} style={{ '--cp-ring-pct': pct }} aria-hidden="true">
                    <span className="cp-ring__hole num">{pct}</span>
                  </div>
                  <div className="cp-progress__meta">
                    <span className="t-eyebrow">Ваш прогресс</span>
                    <span className="t-caption"><span className="num">{doneReq}</span> из <span className="num">{totalReq}</span> {api.lessonsWordGen(totalReq)}</span>
                  </div>
                  <div className="cp-progress__bar" role="progressbar" aria-valuenow={doneReq} aria-valuemin={0} aria-valuemax={totalReq}>
                    <div className="cp-progress__fill" style={{ width: pct + '%' }} />
                  </div>
                  <span className="cp-progress__pct" aria-hidden="true">{pct}%</span>
                </div>
              ) : null}

              {/* Мобильный toggle рельса. В пустом курсе не показываем: кнопка
                  открывала бы пустой Drawer и подтверждала бы обещание «выберите
                  урок», которого в программе нет. */}
              {isMobile && flat.length > 0 && (
                <button type="button" className="cp-rail-toggle" aria-haspopup="dialog" onClick={() => setRailOpen(true)}>
                  <Icon name="menu" size={18} /><span>Программа курса</span>
                  <span className="cp-rail-toggle__count">{doneReq}/{totalReq}</span>
                </button>
              )}

              <div className="cp-layout">
                {!isMobile && <Rail tree={tree} activeId={lessonId} onPick={pick} />}
                <div>
                  {lessonId
                    ? <LessonPane key={lessonId} lessonId={lessonId} frozen={frozen}
                        subscriptionHold={subscriptionHold}
                        onChanged={reloadTree} onForbidden={onLessonForbidden}
                        onProgram={isMobile ? () => setRailOpen(true) : null}
                        onUpgrade={buyCourse} />
                    : !flat.length
                      /* Программа пуста (0 из 0): звать «выберите урок» и слать в
                         список, которого нет, — обещание, которое экран не может
                         выполнить. Называем состояние как есть. */
                      ? <EmptyState icon="book" title="В курсе пока нет уроков"
                          body="Программа ещё наполняется. Как только автор добавит первый урок, он появится здесь." />
                      : <EmptyState icon="book" title="Выберите урок"
                          /* На мобиле программы СЛЕВА нет — она в Drawer по кнопке
                             сверху; текст обязан описывать тот экран, который перед
                             глазами, а не десктопную раскладку. */
                          body={isMobile
                            ? 'Откройте «Программа курса» кнопкой выше и выберите первый доступный урок.'
                            : 'Слева — программа курса. Откройте первый доступный урок.'}
                          action={firstOpen && <Button variant="primary" onClick={() => pick(firstOpen.id)}>Начать урок</Button>} />}

                  {/* sticky-футер навигации (только при открытом и ДОСТУПНОМ уроке).
                      На закрытом уроке футера нет: (а) его середина обещала
                      «Выполните задания урока» про урок, который открыть нельзя
                      вообще, (б) на 380 px он накрывал собой кнопку «Программа
                      курса» — единственную живую кнопку того экрана (замер QA:
                      кнопка y=611…655, футер от y=622.5, elementFromPoint в её
                      центре отдавал .cp-footer). Выход с закрытого урока —
                      программа курса, она и остаётся на виду. */}
                  {lessonId && enrolled && !curLocked && (
                    /* ST-9: середина футера — отдельный узел, а не две распорки:
                       на узком экране она уходит на свою строку, и «Назад» с
                       «Далее» остаются в пределах вьюпорта (раньше «Далее»
                       уезжал за край на 380 px). */
                    <nav className="cp-footer" aria-label="Навигация по уроку" ref={footerRef}>
                      <Button variant="secondary" size="sm" iconLeft="arrow-left" disabled={!prev}
                        onClick={() => prev && pick(prev.id)}>Назад</Button>
                      <div className="cp-footer__mid">
                        {/* «Отметить пройденным» здесь была мёртвой аффордансой:
                            контентный урок (без теста и ДЗ) бэкенд закрывает уже
                            на открытии (services.mark_viewed → recompute_lesson),
                            и кнопка жила доли секунды — до перечитки дерева.
                            Вместо кнопки, которую нечем нажать, — правило. */}
                        {curNode && curNode.progress === 'completed'
                          ? <span className="cp-footer__hint"><Icon name="check-circle" size={15} style={{ color: 'var(--status-ok)' }} />Урок пройден</span>
                          : (curNode && !curNode.has_quiz && !curNode.has_homework
                              ? <span className="cp-footer__hint"><Icon name="info" size={15} />Урок засчитывается при открытии</span>
                              : <span className="cp-footer__hint">Выполните задания урока</span>)}
                      </div>
                      <Button variant="primary" size="sm" iconRight="arrow-right"
                        disabled={!next || !!next.lock} onClick={() => next && pick(next.id)}>Далее</Button>
                    </nav>
                  )}
                </div>
              </div>

              {/* рельс в Drawer на мобиле */}
              {isMobile && (
                <Drawer open={railOpen} onClose={() => setRailOpen(false)} title="Программа курса" size="md">
                  <div>
                    <Rail tree={tree} activeId={lessonId} onPick={pick} inDrawer />
                  </div>
                </Drawer>
              )}
            </>
          )}
        </QueryBoundary>
      </div>
      {buy && <BuyDialog courseId={buy.course.id} courseTitle={buy.course.title}
        coursePrice={buy.course.price_rub} tariffs={buy.tariffs}
        onClose={() => setBuy(null)} />}
    </div>
  );
}

export { CoursePlayer };
// Экспорт ради юнит-тестов (браузер для них не нужен): правила ST-15 «когда
// перечитывать урок по возврату во вкладку безопасно».
export { quizIsDirty, homeworkIsDirty, lessonRefetchAllowed };
